import React, { useState, useEffect } from "react";
import { Button, Typography, Box, Container, Grid } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import DescriptionIcon from "@mui/icons-material/Description";
import ProfilePic from "../../assets/IMG_3462.jpg";
import Resume from "../../assets/General_Software_Engineer.pdf";

const STATS = [
  { value: "4", label: "Hackathon Wins" },
  { value: "17", label: "Projects" },
  { value: "3", label: "Experience Roles" },
];

const HeroSection = () => {
  const names = ["Abhi Ramtel", "\u0905\u092D\u093F \u0930\u092E\u094D\u0924\u0947\u0932"];
  const [currentNameIndex, setCurrentNameIndex] = useState(0);

  const roles = [
    "Backend & Security Engineer",
    "Full-Stack Builder",
    "Hackathon Champion",
    "ML & Security Engineer",
  ];
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRoleIndex((prevIndex) => (prevIndex + 1) % roles.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [roles.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentNameIndex((prevIndex) => (prevIndex + 1) % names.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [names.length]);

  return (
    <Container id="hero">
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          paddingTop: "24px",
        }}
      >
        <Grid
          container
          spacing={4}
          alignItems="center"
          sx={{
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Left side content */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              order: { xs: 2, md: 1 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#7C3AED",
                  fontFamily: "monospace",
                  mb: 2,
                }}
              >
                Hi, my name is
              </Typography>

              {/* Animated name */}
              <Box
                sx={{
                  height: { xs: "3rem", md: "4.5rem", lg: "6rem" },
                  mb: 1,
                  position: "relative",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentNameIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ position: "absolute" }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
                        fontWeight: 700,
                        background:
                          "linear-gradient(90deg, #FFFFFF 0%, #A78BFA 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {names[currentNameIndex]}
                    </Typography>
                  </motion.div>
                </AnimatePresence>
              </Box>

              {/* Animated rotating roles */}
              <Box
                sx={{
                  height: { xs: "3.5rem", md: "4.5rem", lg: "5.5rem" },
                  mb: 3,
                  position: "relative",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentRoleIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ position: "absolute" }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: {
                          xs: "1.8rem",
                          md: "2.5rem",
                          lg: "3rem",
                        },
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 600,
                      }}
                    >
                      {roles[currentRoleIndex]}
                    </Typography>
                  </motion.div>
                </AnimatePresence>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  maxWidth: "600px",
                  mb: 4,
                  color: "rgba(255,255,255,0.6)",
                  lineHeight: 1.7,
                }}
              >
                I’m a computer science graduate from the University at Buffalo
                specializing in backend systems, AI/ML, systems, and full-stack
                engineering. I build high-concurrency solutions across payments,
                LLM tooling, robotics and inspection, and cloud optimization.
              </Typography>

              {/* Stats Bar */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 2, md: 3 },
                  mb: 4,
                  flexWrap: "wrap",
                }}
              >
                {STATS.map((stat, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 0.8,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(124, 58, 237, 0.2)",
                      background: "rgba(124, 58, 237, 0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "1.4rem", md: "1.6rem" },
                        fontWeight: 700,
                        color: "#A78BFA",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: "rgba(255,255,255,0.5)",
                        fontWeight: 500,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box>
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href={Resume}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<DescriptionIcon />}
                  sx={{
                    borderColor: "#7C3AED",
                    color: "#A78BFA",
                    borderWidth: "2px",
                    "&:hover": {
                      borderColor: "#A78BFA",
                      backgroundColor: "rgba(124, 58, 237, 0.1)",
                    },
                  }}
                >
                  Preview my resume
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Right side hero image */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              order: { xs: 1, md: 2 },
              mb: { xs: 4, md: 0 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: "easeOut",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: {
                    xs: "200px",
                    sm: "250px",
                    md: "280px",
                    lg: "350px",
                  },
                  height: {
                    xs: "200px",
                    sm: "250px",
                    md: "280px",
                    lg: "350px",
                  },
                }}
              >
                {/* Decorative border */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "15px",
                    left: "15px",
                    width: "100%",
                    height: "100%",
                    border: "2px solid rgba(124, 58, 237, 0.5)",
                    borderRadius: "10px",
                    zIndex: 0,
                  }}
                />

                {/* Main image container */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    height: "100%",
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 10px 30px -15px rgba(124, 58, 237, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 20px 40px -20px rgba(124, 58, 237, 0.5)",
                      "& > img": {
                        transform: "scale(1.05)",
                      },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={ProfilePic}
                    alt="Abhi - Developer"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      transition: "transform 0.5s ease",
                      transform: "rotate(0deg)",
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      mixBlendMode: "multiply",
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default HeroSection;
